import React from 'react';
import { useSelector } from 'react-redux';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  FieldArrayRenderProps,
  useField,
  Field,
  FieldProps,
} from 'formik';
import * as Yup from 'yup';
import classNames from 'classnames';

import Checkbox from 'common/components/checkbox';
import HfReactHelper from 'common/utils/hf-react-helper';
import * as yupUtils from 'hellospa/common/utils/yup';
import * as types from 'hellospa/page/prep-and-send/data/types';
import { StoreShape } from 'hellospa/redux/types';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import TextField from 'hellospa/foundation/control/text-field';

import styles from './index.scss';
import messages from './intl';
import Row from '../recipient/row';
import Document from './document';
import HighResolutionFaxModal from './high-resolution-fax-modal';
import ChargeModal from './one-time-charge-modal';
import { SectionHeader } from '../header';
import { DragSort } from 'hellospa/components/drag-sort';
import { BannerErrorMessage } from 'hellospa/components/prep-and-send/formik-utils';
import PlusIcon from './icons/plus';

type FaxRecipientSectionProps = {
  formikHelpers: FieldArrayRenderProps,
}

export const FaxRecipientSection = ({ formikHelpers }: FaxRecipientSectionProps) => {
  const intl = useIntl();
  const workflow = useSelector(selectors.getWorkflow);
  const addRecipientAction = useSelector(selectors.getRecipientGenerator);
  const isFaxRequest = workflow.tags.includes(types.WorkflowTags.SignatureRequestFax);

  const [recipients] = useField<types.Recipient[]>('recipients');
  const [sendHighResFax] = useField<boolean>('sendHighResFax');
  const [addCoverPage] = useField<boolean>('addCoverPage');

  const needsCharge = useSelector(selectors.needsCharge);
  const [chargeModalOpen, setChargeModalOpen] = React.useState(false);
  const closeChargeModal = () => setChargeModalOpen(false);


  const allowAdd = useSelector((state:StoreShape) => (
    selectors.allowRecipientAdd(state, recipients.value)
  ));

  React.useEffect(() => {
    setChargeModalOpen(needsCharge);
  }, [needsCharge]);

  if (!isFaxRequest) {
    return null;
  }

  const handleAddFaxRecipient = (
    formikHelpers: FieldArrayRenderProps,
  ) => {
    if (allowAdd && addRecipientAction) {
      formikHelpers.push(addRecipientAction());
    }
  };
  return (
    <React.Fragment>
      <BannerErrorMessage name="recipients" />

      <SectionHeader>
        {intl.formatMessage(messages.headerFaxRecipientSection)}
      </SectionHeader>
      <DragSort
        // I had to use `<DragSort` because `<Row` contains a `<DragHandle`, but
        // we don't actually sort faxes
        component={Row}
        onDrop={(drop) => formikHelpers.move(drop.from, drop.to)}
        data={recipients.value}
        messageValues={() => ({
          // Faxes don't get sorted, so this isn't really used.
          name: '', type: '',
        })}
        getProps={(recipient, index) => ({
          key: recipient.id,
          recipient,
          index,
          type: recipient.type,
          formikHelpers,
          isMeOnly: false,
          allowAccessCode: false,
          allowAttachment: false,
          allowDelete: recipients.value.length > 1,
        })} />

      {allowAdd && (
        <div className={styles.addRecipientContainer}>
          <button
            type="button"
            onClick={() => handleAddFaxRecipient(formikHelpers)}
            className={styles.addRecipient}
            data-qa-ref="recipients.buttons.add_row"
          >
            <PlusIcon className={styles.plusIcon} />
            {intl.formatMessage(messages.addFaxRecipientButton)}
          </button>
        </div>
      )}
      <IncludeOtherItemsSection
        sendHighResFax={sendHighResFax.value}
        addCoverPage={addCoverPage.value} />
      {chargeModalOpen && (
        <ChargeModal
          isOpen={chargeModalOpen}
          close={closeChargeModal} />
      )}
    </React.Fragment>
  );
};

const CoverPage = () => {
  const intl = useIntl();
  const [addCoverPageField] = useField('addCoverPage');
  const isPaidAccount = useSelector(selectors.isPaidAccount);
  const { lockDocumentMessage } = useSelector(selectors.getFlags);
  const pricingUrl = HfReactHelper.urlHelper('info/pricing', 'hellofax');

  if (!addCoverPageField.value) {
    return null;
  }

  return (
    <div className={classNames(styles.fields, styles.coverPage)}>
      <div className={styles.fieldRow}>
        <div>
          <label htmlFor="coverPage.to">
            {intl.formatMessage(messages.coverPageToLabel)}
          </label>
          <Field
            name="coverPage.to"
          >
            {({ field }: FieldProps) => <TextField
              id="coverPage.to"
              data-qa-ref="coverPage.to"
              {...field} />}
          </Field>
        </div>
        <div>
          <label htmlFor="coverPage.from">
            {intl.formatMessage(messages.coverPageFromLabel)}
          </label>
          <Field
            name="coverPage.from"
          >
            {({ field }: FieldProps) => <TextField
              id="coverPage.from"
              data-qa-ref="coverPage.from"
              {...field} />}
          </Field>
        </div>
      </div>
      <div className={styles.fieldRow}>
        <div>
          <label htmlFor="coverPage.message">
            {intl.formatMessage(messages.coverPageMessageLabel)}
          </label>
          <Field
            id="coverPage.message"
            name="coverPage.message"
            component="textarea"
            disabled={lockDocumentMessage}
            rows="4"
            className={styles.field}
            data-qa-ref="coverPage.message" />
        </div>
      </div>
      {!isPaidAccount && (
        <span className={styles.tiny}>
          <FormattedMessage
            id=''
            description='text shown when user is on free account for cover page info'
            defaultMessage='Note: Cover page will include “Sent via HelloFax”. <a>Upgrade now</a> to send without it.'
            values={{
              // eslint-disable-next-line react/display-name
              a: (str: string) => <a href={pricingUrl}>{str}</a>,
            }} />
        </span>
      )}
    </div>
  );
};

type FaxNumberItemsProps = {
  sendHighResFax: boolean,
  addCoverPage: boolean,
}

const FaxNumberItems = ({ sendHighResFax, addCoverPage }: FaxNumberItemsProps) => {
  const intl = useIntl();
  const [highResolutionModalOpen, setHighResolutionModalOpen] = React.useState(false);
  const openHighResolutionModal = () => setHighResolutionModalOpen(true);
  const closeHighResolutionModal = () => setHighResolutionModalOpen(false);
  const handleHighResolutionClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    openHighResolutionModal();
  };

  return (
    <React.Fragment>
      <div className={styles.faxOptionsContainer}>
        <label>
          <Field name="sendHighResFax">
            {({ field, form }: FieldProps) => (
              <Checkbox
                id="fax-recipients-send-high-res-fax"
                indeterminate={false}
                checked={field.value}
                disabled={false}
                className=""
                onChange={() => {
                  form.setFieldValue(field.name, !sendHighResFax);
                }}
                data-qa-ref="recipients.fields.sendHighResFax" />
            )}
          </Field>
          {intl.formatMessage(messages.sendHighResolutionFaxCheckbox)}
          <span onClick={handleHighResolutionClick}>
            <a>{intl.formatMessage(messages.learnMoreLabel)}</a>
          </span>
        </label>
        <label>
          <Field name="addCoverPage">
            {({ field, form }: FieldProps) => (
              <Checkbox
                id="fax-recipients-add-cover-page"
                indeterminate={false}
                checked={field.value}
                disabled={false}
                className=""
                onChange={() => {
                  form.setFieldValue(field.name, !addCoverPage);
                }}
                data-qa-ref="recipients.fields.addCoverPage" />
            )}
          </Field>
          {intl.formatMessage(messages.addCoverPage)}
        </label>
      </div>
      <CoverPage />
      {highResolutionModalOpen && (
        <HighResolutionFaxModal
          isOpen={highResolutionModalOpen}
          close={closeHighResolutionModal} />
      )}
    </React.Fragment>
  );
};

type IncludeOtherItemsSectionProps = {
  sendHighResFax: boolean,
  addCoverPage: boolean,
}

export const getFaxRecipientTypes = (recipients: types.FaxRecipient[]) => {
  let hasFaxNumber = false;
  let hasEmail = false;
  recipients.forEach((recipient) => {
    if (recipient.fax.length > 0) {
      if (Yup.string().email().isValidSync(recipient.fax)) {
        hasEmail = true;
      } else if (yupUtils.phoneNumber().isValidSync(recipient.fax)) {
        hasFaxNumber = true;
      }
    }
  });
  return { hasFaxNumber, hasEmail };
};

export const IncludeOtherItemsSection = (props: IncludeOtherItemsSectionProps) => {
  const intl = useIntl();
  const {
    sendHighResFax,
    addCoverPage,
  } = props;

  const [faxRecipientsField] = useField('recipients');
  const { hasFaxNumber, hasEmail } = getFaxRecipientTypes(faxRecipientsField.value);

  return (
    <React.Fragment>
      {(hasFaxNumber || hasEmail) && (
        <SectionHeader>
          {intl.formatMessage(messages.headerIncludeOtherSecion)}
        </SectionHeader>
      )}
      {hasFaxNumber && (
        <FaxNumberItems
          sendHighResFax={sendHighResFax}
          addCoverPage={addCoverPage} />
      )}
      {hasEmail && (
        <Document useHeader={messages.headerFaxDocument} />
      )}
    </React.Fragment>
  );
};
